

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AddressFilter, MapSearchFilters } from '../../features/inventaires/map-view/map-search-panel';

// Re-export types for easier importing
export type { AddressFilter, MapSearchFilters };

// Utility functions
export const isValidCoordinates = (lat?: number, lng?: number): boolean => {
  return lat !== undefined && lng !== undefined &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180;
};

export const getFilterDisplayName = (key: keyof MapSearchFilters): string => {
  const displayNames: Record<keyof MapSearchFilters, string> = {
    essence: 'Essence',
    espece_id: 'ID Espèce',
    codeSite: 'Code Site',
    address: 'Adresse',
    addressFilter: 'Filtre Adresse',
    ville: 'Ville',
    arbreRemarquable: 'Arbre Remarquable',
    brouillon: 'Brouillon',
    alertes: 'Alertes',
    radius: 'Rayon',
  };
  return displayNames[key] || key;
};

// Default filter values
export const DEFAULT_FILTERS: MapSearchFilters = {
  radius: 1000,
  essence: undefined,
  espece_id: undefined,
  codeSite: undefined,
  address: undefined,
  addressFilter: undefined,
  ville: undefined,
  arbreRemarquable: undefined,
  brouillon: undefined,
  alertes: undefined,
};

export interface FilterState {
  filters: MapSearchFilters;
  setFilters: (filters: MapSearchFilters) => void;
  updateFilter: <K extends keyof MapSearchFilters>(key: K, value: MapSearchFilters[K]) => void;
  setAddress: (address: string, lat?: number, lng?: number) => void;
  setEspeceById: (espece_id: number) => void;
  clearFilters: (onClear?: () => void) => void;
  resetToDefaults: () => void;
  hasActiveFilters: () => boolean;
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      filters: DEFAULT_FILTERS,

      setFilters: (newFilters: MapSearchFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters }
        }));
      },

      updateFilter: <K extends keyof MapSearchFilters>(key: K, value: MapSearchFilters[K]) => {
        set((state) => ({
          filters: { ...state.filters, [key]: value }
        }));
      },

      setAddress: (address: string, lat?: number, lng?: number) => {
        set((state) => ({
          filters: {
            ...state.filters,
            address,
            addressFilter: {
              address,
              lat,
              lng
            }
          }
        }));
      },

      setEspeceById: (espece_id: number) => {
        set((state) => ({
          filters: {
            ...state.filters,
            espece_id,
            essence: undefined // Clear the old essence string when setting by ID
          }
        }));
      },

      clearFilters: (onClear?: () => void) => {
        set({ filters: { radius: DEFAULT_FILTERS.radius } });
        if (onClear) {
          onClear();
        }
      },

      resetToDefaults: () => {
        set({ filters: DEFAULT_FILTERS });
      },

      hasActiveFilters: () => {
        const currentFilters = get().filters;
        return Object.entries(currentFilters).some(([key, value]) => {
          if (key === 'radius') return false; // Radius always has default value
          return value !== undefined && value !== null && value !== '';
        });
      },
    }),
    {
      name: 'dendromap-filter-storage',
      partialize: (state) => ({ filters: state.filters }),
    }
  )
);
